import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import {WarehouseState} from "@/interfaces/state/warehouse";
import {IdMapper} from "@/interfaces/state/base";
import {Lading, Warehouse} from "@/interfaces/entity";
import {PaginationMeta} from "@/interfaces/common";
import {StockInParams, WarehouseParams} from "@/modules/warehouses/params";
import store from "@/store"
import {
    addBillToPickingListDeliveryEmployee, addBillToPickingListDeliveryPartner, addBillToPickingListDeliveryTranshipment,
    allBillLadingNeedStockIn,
    confirmTranshipment,
    listWarehouseByLadingDelivery,
    listWarehouseByLadingReTransport,
    listWarehouseByLadingTranshipment,
    scanBillLadingStockOut, scanBillLadingStockOutReTransport,
    stock,
    stockIn,
    stockOutDeliveryEmployee,
    stockOutDeliveryPartner,
    stockOutReTransport,
    stockOutTranshipment,
    updateBillLadingStockIn, updatePartnerLadingCode
} from "@/modules/warehouses/services";
import {filterData} from "@/shared/services/helper/utils";
import { union } from 'lodash';

@Module({ dynamic: true, name: "warehouse", store })
class WarehouseModule extends VuexModule implements WarehouseState {
    data: IdMapper<Warehouse> = {};
    ids: number[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: WarehouseParams = new WarehouseParams();

    stockedOutWarehouses: Array<Warehouse> = [];

    stockedInLadings: Array<Lading> = [];
    stockInParams: StockInParams = new StockInParams();
    stockInLadings: IdMapper<Lading> = {};
    stockInLadingIds: string[] = [];
    stockInLadingMeta: PaginationMeta = new PaginationMeta();

    @Mutation
    private MAP_WAREHOUSES(payload: any) {
        const idMapper: IdMapper<Warehouse> = {};
        this.meta = payload.meta;
        this.ids = payload.datas?.map((warehouse: Warehouse, index) => {
            idMapper[warehouse.id] = {...warehouse} as any;
            return warehouse.id;
        });
        this.data = idMapper;
    }

    @Mutation
    private DELETE_WAREHOUSES(payload: Array<number>) {
        payload.forEach((id: number) => {
            if (this.ids.includes(id) && this.data[id]) {
                this.ids.splice(this.ids.indexOf(id), 1);
                delete this.data[id];
            }
        })
    }

    @Mutation
    private CLEAR_WAREHOUSES() {
        this.ids = [];
        this.data = {};
    }

    @Mutation
    private ADD_WAREHOUSE({payload, toTop = false}) {
        if (toTop)
            this.ids = union([payload.id], this.ids);
        else
            this.ids = union(this.ids, [payload.id]);
        this.data[payload.id] = {
            ...this.data[payload.id] || {},
            ...filterData(payload)
        };
    }

    @Mutation
    private UPDATE_WAREHOUSE_PARAMS(payload: any) {
        this.params = payload;
    }

    @Mutation
    private SET_STOCKED_OUT_WAREHOUSES(payload: any) {
        this.stockedOutWarehouses = payload;
    }

    @Mutation
    private MAP_STOCK_IN_LADINGS(payload: any) {
        const idMapper: IdMapper<Lading> = {};
        this.stockInLadingMeta = payload.meta;
        this.stockInLadingIds = payload.datas?.map((lading: Lading) => {
            idMapper[lading.rowGuid] = lading;
            return lading.rowGuid;
        });
        this.stockInLadings = idMapper;
    }

    @Mutation
    private SET_STOCK_IN_LADINGS(payload: Array<Lading>) {
        const idMapper: IdMapper<Lading> = {};
        this.stockInLadingIds = payload?.map((lading: Lading) => {
            idMapper[lading.rowGuid] = lading;
            return lading.rowGuid;
        });
        this.stockInLadings = idMapper;
    }

    @Mutation
    private UPDATE_STOCK_IN_LADINGS(payload: Lading) {
        if (this.stockInLadings[payload.rowGuid]) {
            this.stockInLadings = {
                ...this.stockInLadings,
                [payload.rowGuid]: {...this.stockInLadings[payload.rowGuid], ...filterData(payload)}
            } as any;
        }
    }

    @Mutation
    private ADD_STOCK_IN_LADINGS(payload: Lading) {
        this.stockInLadingIds = union([payload.rowGuid], this.stockInLadingIds);
        this.stockInLadings[payload.rowGuid] = payload;
    }

    @Mutation
    private DELETE_STOCK_IN_LADINGS(payload: Array<string>) {
        payload.forEach((id: string) => {
            if (this.stockInLadingIds.includes(id) && this.stockInLadings[id]) {
                this.stockInLadingIds.splice(this.stockInLadingIds.indexOf(id), 1);
                delete this.stockInLadings[id];
            }
        })
    }

    @Mutation
    private CLEAR_STOCK_IN_LADINGS() {
        this.stockInLadingIds = [];
        this.stockInLadings = {};
    }

    @Mutation
    private UPDATE_STOCK_IN_PARAMS(payload: any) {
        this.stockInParams = payload;
    }

    @Mutation
    private UPDATE_PARTNER_LADING_CODE_IN_WAREHOUSE(payload: Lading) {
        const warehouse = Object.values(this.data)
            .find((warehouse: Warehouse) => warehouse.billLading.rowGuid === payload.rowGuid);
        if (warehouse)
            warehouse.billLading.partnerLadingCode = payload.partnerLadingCode;
    }

    @Action({ rawError: true })
    public clearWarehouses() {
        this.CLEAR_WAREHOUSES();
    }

    @Action({ rawError: true })
    public setStockInLadings(payload: Array<Lading>) {
        this.SET_STOCK_IN_LADINGS(payload);
    }

    @Action({ rawError: true })
    public clearStockInLadings() {
        this.CLEAR_STOCK_IN_LADINGS();
    }

    @Action({ rawError: true })
    public async allBillLadingNeedStockIn(params) {
        const { data } = await allBillLadingNeedStockIn(params);
        this.MAP_STOCK_IN_LADINGS(data);
    }

    @Action({ rawError: true })
    public async updateStockInLading(payload: any) {
        const { data } = await updateBillLadingStockIn(payload);
        this.UPDATE_STOCK_IN_LADINGS(data.data);
    }

    @Action({ rawError: true })
    public async stockIn(payload: any) {
        const { data } = await stockIn(payload);
        this.DELETE_STOCK_IN_LADINGS(payload.ladingRowGuids);
        return data.data;
    }

    @Action({ rawError: true })
    public updateStockInParams(payload: any) {
        this.UPDATE_STOCK_IN_PARAMS(payload);
    }

    @Action({ rawError: true })
    public async listWarehouseByLadingDelivery(payload: any) {
        const { data } = await listWarehouseByLadingDelivery(payload);
        this.MAP_WAREHOUSES(data);
        return data.datas;
    }

    @Action({ rawError: true})
    public async listWarehouseByLadingReTransport(payload: any) {
        const {data} = await listWarehouseByLadingReTransport(payload);
        this.MAP_WAREHOUSES(data);
        return data.datas;
    }

    @Action({ rawError: true })
    public async listWarehouseByLadingTranshipment(payload: any) {
        const { data } = await listWarehouseByLadingTranshipment(payload);
        this.MAP_WAREHOUSES(data);
    }

    @Action({ rawError: true })
    public async stockOutDeliveryEmployee(payload: any) {
        const { data } = await stockOutDeliveryEmployee(payload);
        this.DELETE_WAREHOUSES(payload.warehouseIds);
        return data.data;
    }

    @Action({ rawError: true })
    public async stockOutDeliveryPartner(payload: any) {
        const { data } = await stockOutDeliveryPartner(payload);
        this.DELETE_WAREHOUSES(payload.warehouseIds);
        return data.data;
    }

    @Action({ rawError: true })
    public async stockOutTranshipment(payload: any) {
        const { data } = await stockOutTranshipment(payload);
        this.DELETE_WAREHOUSES(payload.warehouseIds);
        return data.data;
    }

    @Action( {rawError: true})
    public async stockOutReTransport(payload: any) {
        const { data } = await stockOutReTransport(payload)
        this.DELETE_WAREHOUSES(payload.warehouseIds)
        return data.data
    }

    @Action({ rawError: true })
    public async confirmTranshipment(payload: any) {
        const { data } = await confirmTranshipment(payload);
        this.DELETE_STOCK_IN_LADINGS(payload.ladingRowGuids);
        return data.data;
    }

    @Action({ rawError: true })
    public addWarehouse({ payload, toTop = false }) {
        this.ADD_WAREHOUSE({ payload, toTop });
    }

    @Action({ rawError: true })
    public async stock(payload: any) {
        const { data } = await stock(payload);
        this.MAP_WAREHOUSES(data);
    }

    @Action({ rawError: true })
    public updateWarehouseParams(payload: any) {
        this.UPDATE_WAREHOUSE_PARAMS(payload);
    }

    @Action({ rawError: true })
    public addStockInLading(payload: any) {
        this.ADD_STOCK_IN_LADINGS(payload);
    }

    @Action({ rawError: true })
    public deleteStockInLading(payload: Array<string>) {
        this.DELETE_STOCK_IN_LADINGS(payload);
    }

    @Action({ rawError: true })
    public deleteWarehouse(payload: Array<number>) {
        this.DELETE_WAREHOUSES(payload);
    }

    @Action({ rawError: true})
    public async scanBillLadingStockOut(ladingCode: string) {
        const { data } = await scanBillLadingStockOut(ladingCode);
        return data.data;
    }

    @Action({ rawError: true})
    public async scanBillLadingStockOutReTransport(ladingCode: string) {
        const { data } = await scanBillLadingStockOutReTransport(ladingCode);
        return data.data;
    }

    @Action({ rawError: true })
    public async updatePartnerLadingCode(payload: any) {
        const { data } = await updatePartnerLadingCode(payload);
        this.UPDATE_PARTNER_LADING_CODE_IN_WAREHOUSE(data.data);
    }

    @Action({ rawError: true })
    public async addBillToPickingListDeliveryEmployee(payload: any) {
        const { data } = await addBillToPickingListDeliveryEmployee(payload);
        this.DELETE_WAREHOUSES(payload.warehouseIds);
        return data.data;
    }

    @Action({ rawError: true })
    public async addBillToPickingListDeliveryPartner(payload: any) {
        const { data } = await addBillToPickingListDeliveryPartner(payload);
        this.DELETE_WAREHOUSES(payload.warehouseIds);
        return data.data;
    }

    @Action({ rawError: true })
    public async addBillToPickingListTranshipment(payload: any) {
        const { data } = await addBillToPickingListDeliveryTranshipment(payload);
        this.DELETE_WAREHOUSES(payload.warehouseIds);
        return data.data;
    }
}

export const WarehouseStore = getModule(WarehouseModule);
